import component from './AsssistantSubmit.vue';
import {
  FormCheckboxValueType,
  FormMultipleChoiceValueType,
  FormTextFieldValueType,
} from '@/interfaces/step-definition';
import { AssistantStepContext } from '@/interfaces/context';
import {
  buildFormCheckbox,
  buildFormMultipleChoiceField,
  buildFormSelect,
  buildFormTextField,
} from '@/lib/forms/builder';
import { ValidationType } from '@/consts/validation-types';
import { sendProductRequest } from '@/lib/submit/send-product-request';
import {
  getProductId,
  getProductRequest,
  getProductType,
} from '~/helper/request-product';
import { getRequestCommentFromContext } from '~/helper/request-comment';
import { withCampaign } from '~/helper/campaign';
import { getRequestAssistantData } from '~/helper/request-assistant-data';
import { StepCategoryId, StepId } from '~/consts/assistant-steps';
import i18n from '@/plugins/i18n';
import { FormFieldGroupId } from '~/consts/assistant-form-field-groups';
import { resetEventData, sendEvent } from '@/lib/analytics/dataLayer';

const contactDataFieldConfig = {
  salutation: [] as FormMultipleChoiceValueType,
  companyName: '' as FormTextFieldValueType,
  firstname: '' as FormTextFieldValueType,
  lastname: '' as FormTextFieldValueType,
  street: '' as FormTextFieldValueType,
  streetNumber: '' as FormTextFieldValueType,
  zipcode: '' as FormTextFieldValueType,
  city: '' as FormTextFieldValueType,
  phone: '' as FormTextFieldValueType,
  email: '' as FormTextFieldValueType,
  invoiceAddressDiffers: [] as FormMultipleChoiceValueType,
  consentContact: false as FormCheckboxValueType,
};

export const CONTACT_DATA_FIELD_NAMES = Object.keys(
  contactDataFieldConfig,
) as Array<keyof typeof contactDataFieldConfig>;

export type ContactDataStepContext = AssistantStepContext &
  typeof contactDataFieldConfig;

export interface AssistantSubmitStepContext extends ContactDataStepContext {
  buildingTypeSingleOrFamily?: FormMultipleChoiceValueType;
  owner?: FormMultipleChoiceValueType;
  usage?: FormMultipleChoiceValueType;
  buildingType?: FormMultipleChoiceValueType;
  solarRoofShape?: FormMultipleChoiceValueType;
}

/**
 * Product names reported in the `products` array of the `store_assistant_lead`
 * event.
 */
const PRODUCT_SOLAR = 'Solar';
const PRODUCT_HEATING = 'Heizung';
const PRODUCT_WALLBOX_CHECK = 'Installationscheck Wallbox';

/**
 * Products of the single product assistants.
 */
const SINGLE_ASSISTANT_PRODUCTS: Partial<Record<StepCategoryId, string[]>> = {
  [StepCategoryId.SOLAR]: [PRODUCT_SOLAR],
  [StepCategoryId.HEATING]: [PRODUCT_HEATING],
  [StepCategoryId.EMOBILITY]: [PRODUCT_WALLBOX_CHECK],
};

/**
 * Maps an interest selected in the combi assistant onto its product name. The
 * order matches the options of ~/steps/combi/combi-interest, so the reported
 * products are independent of the order they were selected in.
 */
const COMBI_INTEREST_PRODUCTS: Array<[string, string]> = [
  ['pv', PRODUCT_SOLAR],
  ['heat-pump', PRODUCT_HEATING],
  ['wallbox', PRODUCT_WALLBOX_CHECK],
];

const config = {
  component,
  hideSkipMessage: () => true,
  id: StepId.ASSISTANT_SUBMIT,
  categoryId: StepCategoryId.SUBMIT,
  fields: [
    buildFormMultipleChoiceField('salutation', {
      component: {
        options: [
          {
            value: 'm',
          },
          {
            value: 'w',
          },
        ],
        columns: 2,
        singleAnswer: true,
      },
      required: true,
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormTextField('firstname', {
      required: true,
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormTextField('lastname', {
      required: true,
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormTextField('street', {
      required: true,
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormTextField('streetNumber', {
      required: true,
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormTextField('zipcode', {
      required: true,
      validation: [ValidationType.postalCodeDE],
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormTextField('city', {
      required: true,
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormTextField('phone', {
      required: true,
      validation: [ValidationType.phoneNumber],
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormTextField('email', {
      component: {
        type: 'email',
      },
      required: true,
      validation: [ValidationType.email],
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormMultipleChoiceField('invoiceAddressDiffers', {
      component: {
        options: [
          {
            value: 'yes',
          },
          {
            value: 'no',
          },
        ],
        columns: 2,
        singleAnswer: true,
      },
      required: true,
      outputFormatter: (data) => ({
        formattedLabel: i18n.t(
          'contactForm.formFields.invoiceAddressDiffers.formattedLabel',
        ) as string,
        formattedValue: data.formattedValue,
      }),
    }),
    buildFormTextField('invoiceAddressStreet', {
      required: false,
      outputFormatter: (data) => ({
        formattedLabel:
          data?.formattedValue !== '–' ? data.formattedLabel : false,
        formattedValue:
          data?.formattedValue !== '–' ? data.formattedValue : false,
      }),
    }),
    buildFormTextField('invoiceAddressStreetNumber', {
      required: false,
      outputFormatter: (data) => ({
        formattedLabel:
          data?.formattedValue !== '–' ? data.formattedLabel : false,
        formattedValue:
          data?.formattedValue !== '–' ? data.formattedValue : false,
      }),
    }),
    buildFormTextField('invoiceAddressZipcode', {
      required: false,
      validation: [ValidationType.postalCodeDE],
      outputFormatter: (data) => ({
        formattedLabel:
          data?.formattedValue !== '–' ? data.formattedLabel : false,
        formattedValue:
          data?.formattedValue !== '–' ? data.formattedValue : false,
      }),
    }),
    buildFormTextField('invoiceAddressCity', {
      required: false,
      outputFormatter: (data) => ({
        formattedLabel:
          data?.formattedValue !== '–' ? data.formattedLabel : false,
        formattedValue:
          data?.formattedValue !== '–' ? data.formattedValue : false,
      }),
    }),
    buildFormTextField('note', {
      required: false,
      outputFormatter: (data) => {
        if (data.formattedValue === '0' || data.formattedValue === '–') {
          return {
            formattedLabel: false,
            formattedValue: false,
          };
        }
        return {
          formattedLabel: data?.formattedLabel,
          formattedValue: data?.formattedValue,
        };
      },
      groupId: FormFieldGroupId.CONTACT,
    }),
    buildFormSelect('referral', {
      component: {
        items: [
          'personal-visit',
          'promotion-internet',
          'promotion-facebook',
          'advertisement',
          'recommendation',
          'e-store',
          'fair',
          'canvasser',
          'invoice-inclosure',
        ],
      },
      outputFormatter: (data) => {
        if (data.formattedValue === '' || data.formattedValue === '–') {
          return {
            formattedLabel: false,
            formattedValue: false,
          };
        }
        return {
          formattedLabel: i18n.t(
            'assistantSubmit.formFields.referral.formattedLabel',
          ) as string,
          formattedValue: data?.formattedValue,
        };
      },
      groupId: FormFieldGroupId.OTHER,
    }),
    buildFormMultipleChoiceField('solarRoofShape', {
      component: {
        options: [
          {
            value: 'heatPump',
          },
          {
            value: 'wallbox',
          },
        ],
        columns: 1,
        singleAnswer: false,
      },
      groupId: FormFieldGroupId.OTHER,
      required: false,
      outputFormatter: (data) => {
        if (data.formattedValue === '0' || data.formattedValue === '–') {
          return {
            formattedLabel: false,
            formattedValue: false,
          };
        }
        return {
          formattedLabel: i18n.t(
            'assistantSubmit.formFields.solarRoofShape.formattedLabel',
          ) as string,
          formattedValue: data?.formattedValue,
        };
      },
    }),
    buildFormMultipleChoiceField('buildingType', {
      component: {
        options: [
          {
            value: 'single',
          },
          {
            value: 'semi-detached',
          },
          {
            value: 'townhouse',
          },
          {
            value: 'multi',
          },
        ],
        columns: 1,
        singleAnswer: true,
      },
      groupId: FormFieldGroupId.OBJECT,
      required: false,
      outputFormatter: (data) => {
        if (data.formattedValue === '0' || data.formattedValue === '–') {
          return {
            formattedLabel: false,
            formattedValue: false,
          };
        }
        return {
          formattedLabel: i18n.t(
            'assistantSubmit.formFields.buildingType.formattedLabel',
          ) as string,
          formattedValue: data?.formattedValue,
        };
      },
    }),
    buildFormMultipleChoiceField('buildingTypeSingleOrFamily', {
      component: {
        options: [
          {
            value: 'singleFamily',
          },
          {
            value: 'multiFamily',
          },
        ],
        columns: 1,
        singleAnswer: true,
      },
      groupId: FormFieldGroupId.OBJECT,
      required: true,
      outputFormatter: (data) => {
        if (data.formattedValue === '0' || data.formattedValue === '–') {
          return {
            formattedLabel: false,
            formattedValue: false,
          };
        }
        return {
          formattedLabel: i18n.t(
            'assistantSubmit.formFields.buildingTypeSingleOrFamily.formattedLabel',
          ) as string,
          formattedValue: data?.formattedValue,
        };
      },
    }),
    buildFormMultipleChoiceField('owner', {
      component: {
        options: [
          {
            value: 'yes',
          },
          {
            value: 'no',
          },
        ],
        columns: 1,
        singleAnswer: true,
      },
      groupId: FormFieldGroupId.OBJECT,
      required: true,
      outputFormatter: (data) => {
        if (data.formattedValue === '0' || data.formattedValue === '–') {
          return {
            formattedLabel: false,
            formattedValue: false,
          };
        }
        return {
          formattedLabel: i18n.t(
            'assistantSubmit.formFields.owner.formattedLabel',
          ) as string,
          formattedValue: data?.formattedValue,
        };
      },
    }),
    buildFormMultipleChoiceField('usage', {
      component: {
        options: [
          {
            value: 'private',
          },
          {
            value: 'commercial',
          },
        ],
        columns: 1,
        singleAnswer: true,
      },
      groupId: FormFieldGroupId.OBJECT,
      required: true,
      outputFormatter: (data) => {
        if (data.formattedValue === '0' || data.formattedValue === '–') {
          return {
            formattedLabel: false,
            formattedValue: false,
          };
        }
        return {
          formattedLabel: i18n.t(
            'assistantSubmit.formFields.usage.formattedLabel',
          ) as string,
          formattedValue: data?.formattedValue,
        };
      },
    }),
    buildFormCheckbox('consentContact', {
      component: {
        showLabel: true,
      },
      hideLabel: true,
      required: false,
      outputFormatter: () => ({
        formattedLabel: false,
        formattedValue: false,
      }),
      groupId: FormFieldGroupId.OBJECT,
    }),
  ],
  submit: async ({ i18n, steps, assistantContext }): Promise<void> => {
    try {
      const assistant = document.querySelector(
        '.sidebar header ul > li.active',
      ) as HTMLLIElement;

      const categoryId = assistant.getAttribute(
        'data-category',
      ) as StepCategoryId;

      /*
      ###############################################################################
      ########################### send product request ##############################
      ###############################################################################
      */

      const requestComment = withCampaign(
        getRequestCommentFromContext(steps, assistantContext, [
          categoryId,
          StepCategoryId.SUBMIT,
        ]),
      );

      const assistantData = getRequestAssistantData(steps, assistantContext, [
        categoryId,
        StepCategoryId.SUBMIT,
      ]);

      // console.log(requestComment);

      const contactData = assistantContext.assistantSubmit;
      const salutation = i18n.t(
        `assistantSubmit.formFields.salutation.options.${contactData.salutation[0]}.label`,
      );

      const productId = getProductId(categoryId);
      const productType = getProductType(categoryId);
      const productRequest = getProductRequest(
        contactData,
        requestComment,
        productId,
        productType,
        assistantData,
        salutation,
      );

      // console.log(mailData);
      // console.log(productRequest);

      await sendProductRequest({
        productRequest,
        shopHost: process.env.VUE_APP_STAWAG_HOST,
        shopApiKey: process.env.VUE_APP_STAWAG_API_KEY,
        shopUseBasicAuth: process.env.VUE_APP_STAWAG_USE_BASIC_AUTH === 'true',
        shopBasicAuth:
          process.env.VUE_APP_STAWAG_USE_BASIC_AUTH === 'true'
            ? process.env.VUE_APP_STAWAG_BASIC_AUTH
            : undefined,
        s3BucketName: process.env.VUE_APP_ASSISTANT_S3_BUCKET,
      });

      /**
       *  Google Tag Manager push dataLayer
       */

      const isCombi = categoryId === StepCategoryId.COMBI;
      const combiInterests =
        assistantContext.combiInterest?.combiInterest ?? [];
      const products = isCombi
        ? COMBI_INTEREST_PRODUCTS.filter(([interest]) =>
            combiInterests.includes(interest),
          ).map(([, product]) => product)
        : SINGLE_ASSISTANT_PRODUCTS[categoryId] ?? [];

      if (products.length) {
        // GTM keeps the variables of a previous push in its merged state, so
        // submitting a second assistant on the same page would otherwise
        // report the products of both.
        resetEventData(['products', 'type']);
        sendEvent({
          eventName: 'store_assistant_lead',
          products,
          type: isCombi ? 'kombi' : 'single',
        });
      }

      return;
    } catch (error) {
      let errorMessage = i18n.t('message.error').toString();
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      // eslint-disable-next-line no-console
      console.error(errorMessage);

      throw new Error(errorMessage);
    }
  },
};

export default config;
